import {FluentNumber} from "@fluent/bundle";
import {Localized, useLocalization} from "@fluent/react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
	Box,
	Collapse,
	IconButton,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	alpha,
	tableRowClasses,
} from "@mui/material";
import type {TableRowProps} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";

import ContentItemTitle from "../analytics/content/ContentItemTitle";
import ContentItemTitlePlaceholder from "../analytics/content/ContentItemTitlePlaceholder";
import Widget from "../analytics/widget/Widget";
import WidgetBody from "../analytics/widget/WidgetBody";
import WidgetTitle from "../analytics/widget/WidgetTitle";
import {getExerciseTypeDescription} from "../exerciseBank/exerciseTypeDescriptions";
import {resolvePath} from "../../helpers/pathHelpers";
import {requiresExplicitSessionStart} from "../../store/chapters/Chapter";
import selectChapterExercise from "../../store/chapterExercises/selectChapterExercise";
import {getChapterContent} from "../../store/chapters/getChapterContent";
import selectChapter from "../../store/chapters/selectChapter";
import selectChapterContentStatus from "../../store/chapters/selectChapterContentStatus";
import selectChapterSectionKeys from "../../store/chapters/selectChapterSectionKeys";
import selectCourseChapterKeys from "../../store/chapters/selectCourseChapterKeys";
import CourseType from "../../store/courses/CourseType";
import selectCourse from "../../store/courses/selectCourse";
import selectCourseChaptersFetchStatus from "../../store/chapters/selectCourseChaptersFetchStatus";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {keyProvider} from "../../store/keyProvider";
import makeSelectSection from "../../store/sections/makeSelectSection";
import selectSubsectionKeys from "../../store/sections/selectSubsectionKeys";
import randomN from "../../utils/randomN";
import TableCell from "../../utils/tables/Cell";
import {Column, ColumnDefs} from "../../utils/tables/Head";
import LoadingErrorState from "../../utils/tables/LoadingErrorState";

import type FetchStatus from "../../store/FetchStatus";
import type {
	ContentStudyStats,
	ChapterStudyStats,
	ExerciseStudyStats,
} from "../../store/services/analyticsService";
import {analyticsService} from "../../store/services/analyticsService";

function ContentQualityOverviewWidget(props: {
	courseId: number;
	onRefetchChapters: () => void;
}) {
	return (
		<Widget id="content-quality-overview">
			<WidgetTitle>
				<Localized id="content-quality-overview-widget-title">
					Content quality overview
				</Localized>
			</WidgetTitle>
			<WidgetBody>
				<OverviewTable
					courseId={props.courseId}
					onRefetchChapters={props.onRefetchChapters}
				/>
			</WidgetBody>
		</Widget>
	);
}

const columnKeys = [
	"contentItem",
	"completion",
	"completionTime",
	"score",
] as const;

const columnDefs: ColumnDefs<typeof columnKeys[number]> = {
	contentItem: {
		name: (
			<Localized id="content-quality-overview-widget-column-content">
				Content
			</Localized>
		),
		width: 0.3,
	},
	completion: {
		name: (
			<Localized id="content-quality-overview-widget-column-completion">
				Completion %
			</Localized>
		),
		align: "center",
	},
	completionTime: {
		name: (
			<Localized id="content-quality-overview-widget-column-completion-time">
				Avg. Completion Time
			</Localized>
		),
		align: "center",
	},
	score: {
		name: (
			<Localized id="content-quality-overview-widget-column-score">
				Avg. Score
			</Localized>
		),
		align: "center",
	},
};

const actionColumn: Column<string> = {
	name: "",
	width: (theme) => `calc(1.5rem + ${theme.spacing(3.5)})`,
	padding: "checkbox",
};

const columnNumber = columnKeys.length + 1;

function OverviewTable(props: {
	courseId: number;
	onRefetchChapters: () => void;
}) {
	const {courseId} = props;

	const courseKey = keyProvider.course(courseId);

	const [courseStats, setCourseStats] = useState<{
		[chapterKey: string]: ChapterStudyStats | undefined;
	}>({});

	const [statsFetchStatus, setStatsFetchStatus] = useState<FetchStatus>("none");

	const chapters = useAppSelector((state) =>
		selectCourseChapterKeys(state, courseKey)
	);

	const chaptersFetchStatus = useAppSelector((state) =>
		selectCourseChaptersFetchStatus(state, courseKey)
	);

	const courseType = useAppSelector(
		(state) => selectCourse(state, courseKey)?.type || CourseType.Ordinary
	);

	async function fetchCourseStats(courseId: number) {
		setStatsFetchStatus("pending");

		try {
			const stats = await analyticsService.getCourseContentStudyStats(courseId);

			const statsByCh: {[chapterKey: string]: ChapterStudyStats} = {};
			stats.chapters.forEach((s) => {
				const key = keyProvider.chapter(s.chapterId);

				statsByCh[key] = s;
			});

			setCourseStats(statsByCh);
			setStatsFetchStatus("succeeded");
		} catch {
			setStatsFetchStatus("failed");
		}
	}

	const statsShouldBeFetched = statsFetchStatus === "none";

	useEffect(() => {
		if (statsShouldBeFetched) {
			fetchCourseStats(courseId);
		}
	}, [courseId, statsShouldBeFetched]);

	const fetchFailed =
		chaptersFetchStatus === "failed" || statsFetchStatus === "failed";

	return (
		<TableContainer sx={{minHeight: fetchFailed ? undefined : 550}}>
			<Table stickyHeader sx={{tableLayout: "fixed", minWidth: 600}}>
				<TableHead>
					<TableRow>
						<TableCell def={columnDefs.contentItem}>
							{columnDefs.contentItem.name}
						</TableCell>
						<TableCell def={columnDefs.completion}>
							{columnDefs.completion.name}
						</TableCell>
						<TableCell def={columnDefs.completionTime}>
							{columnDefs.completionTime.name}
						</TableCell>
						<TableCell def={columnDefs.score}>
							{columnDefs.score.name}
						</TableCell>
						<TableCell def={actionColumn} />
					</TableRow>
				</TableHead>

				<TableBody>
					{chaptersFetchStatus === "pending" && (
						<>
							<RowPlaceholder />
							<RowPlaceholder />
							<RowPlaceholder />
						</>
					)}

					{chaptersFetchStatus === "failed" && (
						<LoadingErrorState
							columnNumber={columnNumber}
							description={
								<Localized id="content-quality-overview-widget-error-fetch-chapters">
									Something has gone wrong, and we cannot load chapters
								</Localized>
							}
							onReload={props.onRefetchChapters}
						/>
					)}

					{chaptersFetchStatus === "succeeded" &&
						statsFetchStatus === "failed" && (
							<LoadingErrorState
								description={
									<Localized id="content-quality-overview-widget-error-fetch-statistics">
										Something has gone wrong, and we cannot load course
										statistics
									</Localized>
								}
								columnNumber={columnNumber}
								onReload={() => fetchCourseStats(courseId)}
							/>
						)}

					{chaptersFetchStatus === "succeeded" &&
						statsFetchStatus !== "failed" &&
						chapters.map((key) => {
							if (
								statsFetchStatus === "none" ||
								statsFetchStatus === "pending"
							) {
								return (
									<ChapterQualityRow
										key={key}
										courseId={courseId}
										chapterKey={key}
										courseType={courseType}
									/>
								);
							}

							const stats = courseStats[key];
							if (!stats) {
								return null;
							}

							return (
								<ChapterQualityRow
									key={key}
									courseId={courseId}
									chapterKey={key}
									courseType={courseType}
									stats={stats}
								/>
							);
						})}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

function ChapterQualityRow(props: {
	courseId: number;
	chapterKey: string;
	courseType: CourseType;

	stats?: ChapterStudyStats;
}) {
	const {chapterKey, stats} = props;

	const chapter = useAppSelector((state) => selectChapter(state, chapterKey));

	const [expanded, setExpanded] = useState(false);

	const {l10n} = useLocalization();

	if (!chapter) {
		return null;
	}

	const chapterPath = `/courses/${props.courseId}/management/content/chapters/${chapter.id}`;

	const title = (
		<ContentItemTitle
			primary={`${chapter.number} ${chapter.title}`}
			secondary={
				requiresExplicitSessionStart(props.courseType, chapter) ? (
					<Localized id="content-quality-overview-widget-content-item-type-test">
						Test
					</Localized>
				) : (
					<Localized id="content-quality-overview-widget-content-item-type-chapter">
						Chapter
					</Localized>
				)
			}
			link={chapterPath}
		/>
	);

	if (!stats) {
		return <RowPlaceholder title={title} />;
	}

	return (
		<>
			<QualityRow>
				<TableCell def={columnDefs.contentItem}>{title}</TableCell>

				{stats.exercises.length > 0 ? (
					<MetricsCells
						completion={stats.values[0].value}
						completionTime={stats.values[1].value}
						score={stats.values[2].value}
						maxScore={stats.values[2].bound ?? 0}
					/>
				) : (
					<TableCell colSpan={3} />
				)}

				<TableCell def={actionColumn}>
					<IconButton
						aria-label={
							expanded
								? l10n.getString(
										"content-quality-overview-widget-action-collapse-chapter"
								  )
								: l10n.getString(
										"content-quality-overview-widget-action-expand-chapter"
								  )
						}
						onClick={() => setExpanded((prev) => !prev)}
					>
						{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</TableCell>
			</QualityRow>

			<TableRow>
				<TableCell
					colSpan={columnNumber}
					sx={{padding: 0, borderBottom: "unset"}}
				>
					<Collapse in={expanded} unmountOnExit>
						{chapter.structureType !== "sections" ? (
							<ChapterHasOldStructureWarning />
						) : (
							<ChapterContentRows
								courseId={props.courseId}
								chapterId={chapter.id}
								chapterPath={chapterPath}
								chapterNumber={chapter.number}
								chapterKey={chapterKey}
								stats={stats.exercises}
							/>
						)}
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
}

function ChapterContentRows(props: {
	courseId: number;
	chapterId: number;
	chapterPath: string;
	chapterNumber: number;
	chapterKey: string;
	stats: ExerciseStudyStats[];
}) {
	const {courseId, chapterId, chapterKey} = props;

	const sectionKeys = useAppSelector((state) =>
		selectChapterSectionKeys(state, props.chapterKey)
	);

	const contentFetchStatus = useAppSelector((state) =>
		selectChapterContentStatus(state, chapterKey)
	);

	const dispatch = useAppDispatch();

	const contentShouldBeFetched = contentFetchStatus === "none";

	useEffect(() => {
		if (contentShouldBeFetched && chapterId) {
			dispatch(
				getChapterContent({
					courseId,
					chapterId,
					opts: {noNotificationOnError: true},
				})
			);
		}
	}, [chapterId, courseId, dispatch, contentShouldBeFetched]);

	return (
		<Table sx={{tableLayout: "fixed", width: 1}}>
			<TableBody>
				{contentFetchStatus === "pending" && <RowPlaceholder indentation={4} />}

				{contentFetchStatus === "failed" && (
					<LoadingErrorState
						columnNumber={columnNumber}
						description={
							<Localized id="content-quality-overview-widget-error-fetch-chapter-content">
								Something has gone wrong, and we cannot load chapter content
							</Localized>
						}
						onReload={() =>
							dispatch(
								getChapterContent({
									courseId,
									chapterId,
									opts: {noNotificationOnError: true},
								})
							)
						}
					/>
				)}

				{sectionKeys.map((key) => (
					<SectionQualityRow
						key={key}
						chapterPath={props.chapterPath}
						chapterNumber={props.chapterNumber}
						sectionKey={key}
						stats={props.stats}
					/>
				))}
			</TableBody>
		</Table>
	);
}

function ChapterHasOldStructureWarning() {
	return (
		<Stack sx={{alignItems: "center", py: 3}}>
			<Typography variant="subtitle2">
				<Localized id="content-quality-overview-widget-notice-old-structure">
					Chapter has old structure
				</Localized>
			</Typography>
		</Stack>
	);
}

function SectionQualityRow(props: {
	chapterPath: string;
	chapterNumber: number;
	sectionKey: string;
	stats: ExerciseStudyStats[];
}) {
	const selectSection = useMemo(makeSelectSection, []);
	const section = useAppSelector((state) =>
		selectSection(state, props.sectionKey)
	);

	const subsectionKeys = useAppSelector((state) =>
		selectSubsectionKeys(state, props.sectionKey)
	);

	const [expanded, setExpanded] = useState(false);

	const {l10n} = useLocalization();

	if (!section) {
		return null;
	}

	const number = `${props.chapterNumber}.${section.number}`;
	const title = `${number} ${section.title}`;

	const sectionPath = resolvePath(props.chapterPath, `/sections/${section.id}`);

	return (
		<>
			<QualityRow>
				<TableCell def={columnDefs.contentItem} sx={{pl: 4}}>
					<ContentItemTitle
						primary={title}
						secondary={
							<Localized id="content-quality-overview-widget-content-item-type-section">
								Section
							</Localized>
						}
						link={sectionPath}
					/>
				</TableCell>

				<TableCell colSpan={3} />

				<TableCell def={actionColumn}>
					{subsectionKeys.length > 0 && (
						<IconButton
							aria-label={
								expanded
									? l10n.getString(
											"content-quality-overview-widget-action-collapse-section"
									  )
									: l10n.getString(
											"content-quality-overview-widget-action-expand-section"
									  )
							}
							onClick={() => setExpanded((prev) => !prev)}
						>
							{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
						</IconButton>
					)}
				</TableCell>
			</QualityRow>

			<TableRow>
				<TableCell
					colSpan={columnNumber}
					sx={{padding: 0, borderBottom: "unset"}}
				>
					<Collapse in={expanded} unmountOnExit mountOnEnter>
						<SectionContentRows
							subsectionKeys={subsectionKeys}
							sectionNumber={number}
							sectionPath={sectionPath}
							stats={props.stats}
						/>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
}

function SectionContentRows(props: {
	subsectionKeys: string[];
	sectionNumber: string;
	sectionPath: string;
	stats: ExerciseStudyStats[];
}) {
	return (
		<Table sx={{tableLayout: "fixed", width: 1}}>
			<TableBody>
				{props.subsectionKeys.map((key) => (
					<SubsectionQualityRow
						key={key}
						sectionNumber={props.sectionNumber}
						sectionPath={props.sectionPath}
						subsectionKey={key}
						stats={props.stats}
					/>
				))}
			</TableBody>
		</Table>
	);
}

function SubsectionQualityRow(props: {
	sectionNumber: string;
	sectionPath: string;
	subsectionKey: string;
	stats: ExerciseStudyStats[];
}) {
	const exercise = useAppSelector((state) =>
		selectChapterExercise(state, props.subsectionKey)
	);

	const title = `${props.sectionNumber}.${exercise.number} ${exercise.title}`;

	const type = getExerciseTypeDescription(exercise.type);

	const stats = props.stats.find((s) => s.exerciseId === exercise.id);

	return (
		<QualityRow>
			<TableCell def={columnDefs.contentItem} sx={{pl: 4.5}}>
				<ContentItemTitle
					primary={title}
					secondary={type.displayName}
					link={props.sectionPath + `#subsection-${exercise.id}`}
					icon={type.icon}
				/>
			</TableCell>

			{stats ? (
				<MetricsCells
					completion={stats.values[0].value}
					completionTime={stats.values[1].value}
					score={stats.values[2].value}
					maxScore={stats.values[2].bound ?? 0}
				/>
			) : (
				<TableCell colSpan={3} />
			)}

			<TableCell def={actionColumn} />
		</QualityRow>
	);
}

function QualityRow(props: TableRowProps & {highlighted?: boolean}) {
	const {highlighted, ...row} = props;

	return (
		<TableRow
			hover
			sx={[
				Boolean(highlighted) && {
					background: (theme) =>
						alpha(theme.palette.error.main, theme.palette.action.hoverOpacity),
					[`&.${tableRowClasses.hover}:hover`]: {
						background: (theme) =>
							alpha(
								theme.palette.error.main,
								theme.palette.action.selectedOpacity
							),
					},
				},
			]}
			{...row}
		/>
	);
}

const percentFormat = {maximumFractionDigits: 2};
const scoreFormat = {maximumFractionDigits: 2};

function MetricsCells(props: {
	completion: number;
	completionTime: number;
	score: number;
	maxScore: number;
}) {
	const hours = Math.floor(props.completionTime / (60 * 60));
	const minutes = Math.floor((props.completionTime / 60) % 60);
	const seconds = Math.floor(props.completionTime % 60);

	return (
		<>
			<TableCell def={columnDefs.completion}>
				<Localized
					id="content-quality-overview-widget-column-completion-value"
					vars={{value: new FluentNumber(props.completion, percentFormat)}}
				>{`${props.completion}`}</Localized>
			</TableCell>
			<TableCell def={columnDefs.completionTime}>
				<Localized
					id="content-quality-overview-widget-column-completion-time-value"
					vars={{hours, minutes, seconds}}
				>{`${props.completionTime}s`}</Localized>
			</TableCell>
			<TableCell def={columnDefs.score}>
				<Localized
					id="content-quality-overview-widget-column-score-value"
					vars={{
						value: new FluentNumber(props.score, scoreFormat),
						bound: props.maxScore,
					}}
				>{`${props.score} / ${props.maxScore}`}</Localized>
			</TableCell>
		</>
	);
}

function RowPlaceholder(props: {
	indentation?: number;
	title?: React.ReactNode;
}) {
	return (
		<TableRow>
			<TableCell def={columnDefs.contentItem} sx={{pl: props.indentation}}>
				{props.title ? props.title : <ContentItemTitlePlaceholder />}
			</TableCell>

			{[3, 9, 2].map((l, i) => (
				<TableCell key={i}>
					<Box sx={{display: "flex", justifyContent: "center"}}>
						<Typography variant="inherit">
							<Skeleton width={`${l}ch`} />
						</Typography>
					</Box>
				</TableCell>
			))}

			<TableCell def={actionColumn} />
		</TableRow>
	);
}

export default ContentQualityOverviewWidget;
